import {
  Button,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  Paper,
  Select,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { FEDERAL_STATES } from "@msc/shared";

import { useEmployees } from "../../hooks/useEmployees.js";
import { toLocalDateString } from "../../lib/date.js";
import {
  useAddTeamMembers,
  useDiscardTeamMembership,
  useEndTeamMembership,
  useRedateTeamMembership,
  useSegmentTeam,
} from "../../hooks/useKpi.js";
import {
  useAddHoursPeriod,
  useCreateEmployee,
  useDeleteHoursPeriod,
  useHoursPeriods,
  useSaveEmployeeProfile,
} from "../../hooks/useUsers.js";
import type { TeamMember } from "../../hooks/useKpi.js";

/**
 * Team-Tab des Bereichs-Cockpits: Mitglieder des Bereichs (Home-Bereich =
 * Segment) mit Inline-Profilpflege, Hinzufügen bestehender Mitarbeiter und
 * Neuanlage — alles an EINEM Ort, ohne Umweg über die Nutzerverwaltung.
 * Mitgliedschaften sind DATIERT (Eintritt ab / Austritt zum): die Kapazität
 * rechnet historisch korrekt, Ehemalige bleiben als Historie sichtbar.
 * Rechte: BL/Stellv./GL/Admin (Server-Gate).
 */

// Über `toLocalDateString` statt `toISOString` — sonst würde der Vorbelegungs-
// Standardwert kurz nach lokaler Mitternacht auf den Vortag zurückrutschen
// (dieselbe Ursache wie Befund B1).
const isoToday = () => toLocalDateString(new Date());

const fmtDate = (iso: string | null) =>
  iso == null
    ? "–"
    : new Date(iso).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

const CAREER_LEVELS = [
  "Professional Consultant",
  "Expert Consultant",
  "Senior Consultant",
  "Managing Consultant",
  "Manager",
  "Senior Manager",
  "Bereichsleiter",
];

export function TeamPanel({ segmentId }: { segmentId: string }) {
  const { t } = useTranslation();
  const { data: team, isLoading } = useSegmentTeam(segmentId);
  const { data: allEmployees } = useEmployees();
  const saveProfile = useSaveEmployeeProfile();
  const createEmployee = useCreateEmployee();

  const [addTargets, setAddTargets] = useState<string[]>([]);
  const [addFrom, setAddFrom] = useState(isoToday());
  const [periodsTarget, setPeriodsTarget] = useState<TeamMember | null>(null);
  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null);
  const [sinceTarget, setSinceTarget] = useState<TeamMember | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");

  const addTeamMembers = useAddTeamMembers();

  const members = team?.members ?? [];
  const former = team?.former ?? [];
  const memberIds = new Set(members.map((m) => m.employeeId));
  const addOptions = (allEmployees ?? [])
    .filter((e) => !memberIds.has(e.id))
    .map((e) => ({ value: e.id, label: e.fullName }));

  const assignToSegment = () => {
    if (addTargets.length === 0 || !addFrom) return;
    addTeamMembers.mutate(
      { segmentId, employeeIds: addTargets, validFrom: addFrom },
      { onSuccess: () => setAddTargets([]) },
    );
  };

  const updateMember = (
    m: TeamMember,
    patch: Partial<{
      careerLevel: string | null;
      federalState: string;
      hoursPerDay: number;
      segmentId: string | null;
    }>,
  ) => {
    saveProfile.mutate({
      employeeId: m.employeeId,
      careerLevel: patch.careerLevel !== undefined ? patch.careerLevel : m.careerLevel,
      federalState: patch.federalState ?? m.federalState,
      hoursPerDay: patch.hoursPerDay ?? m.hoursPerDay,
      segmentId: patch.segmentId !== undefined ? patch.segmentId : segmentId,
      mentorEmployeeId: m.mentorEmployeeId,
      yppDate: m.yppDate,
    });
  };

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" mb="sm">
        <Text fw={600} size="sm">
          {t("pages.kpi.team.title", { count: members.length })}
        </Text>
        <Group gap="xs">
          <MultiSelect
            size="xs"
            placeholder={t("pages.kpi.team.addPlaceholder")}
            value={addTargets}
            data={addOptions}
            searchable
            clearable
            onChange={setAddTargets}
            style={{ minWidth: 260 }}
          />
          <TextInput
            size="xs"
            type="date"
            label={undefined}
            title={t("pages.kpi.team.joinFromLabel")}
            value={addFrom}
            onChange={(e) => setAddFrom(e.currentTarget.value)}
            style={{ width: 140 }}
          />
          <Button
            size="compact-sm"
            disabled={addTargets.length === 0 || !addFrom}
            loading={addTeamMembers.isPending}
            onClick={assignToSegment}
          >
            {t("pages.kpi.team.addButton", { count: addTargets.length })}
          </Button>
          <Button
            size="compact-sm"
            variant="light"
            onClick={() => setCreateOpen(true)}
          >
            {t("pages.settings.users.createEmployeeButton")}
          </Button>
        </Group>
      </Group>

      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("pages.kpi.team.colName")}</Table.Th>
            <Table.Th>{t("pages.kpi.team.colSince")}</Table.Th>
            <Table.Th>{t("pages.kpi.team.colCareerLevel")}</Table.Th>
            <Table.Th>{t("pages.kpi.team.colFederalState")}</Table.Th>
            <Table.Th>{t("pages.kpi.team.colHours")}</Table.Th>
            <Table.Th style={{ width: 190 }} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {members.map((m) => (
            <Table.Tr key={m.employeeId}>
              <Table.Td>
                <Text size="sm" fw={500}>
                  {m.fullName}
                </Text>
                <Text size="xs" c="dimmed">
                  {m.email}
                </Text>
              </Table.Td>
              <Table.Td>
                <Group gap={2} wrap="nowrap">
                  <Text size="xs">{fmtDate(m.memberSince)}</Text>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    px={4}
                    title={t("pages.kpi.team.sinceEditTitle", {
                      name: m.fullName,
                    })}
                    onClick={() => setSinceTarget(m)}
                  >
                    ✎
                  </Button>
                </Group>
                {m.memberUntil && (
                  <Text size="xs" c="orange" fw={600}>
                    {t("pages.kpi.team.untilLabel", {
                      date: fmtDate(m.memberUntil),
                    })}
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                <Select
                  size="xs"
                  value={m.careerLevel}
                  data={CAREER_LEVELS}
                  searchable
                  clearable
                  onChange={(v) => updateMember(m, { careerLevel: v })}
                  style={{ minWidth: 190 }}
                />
              </Table.Td>
              <Table.Td>
                <Select
                  size="xs"
                  value={m.federalState}
                  data={[...FEDERAL_STATES]}
                  searchable
                  onChange={(v) => v && updateMember(m, { federalState: v })}
                  style={{ width: 90 }}
                />
              </Table.Td>
              <Table.Td>
                <NumberInput
                  size="xs"
                  value={m.hoursPerDay}
                  min={0}
                  max={12}
                  step={0.5}
                  onChange={(v) =>
                    v !== "" && updateMember(m, { hoursPerDay: Number(v) })
                  }
                  style={{ width: 90 }}
                />
              </Table.Td>
              <Table.Td>
                <Group gap={4} wrap="nowrap">
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    onClick={() => setPeriodsTarget(m)}
                  >
                    {t("pages.kpi.team.periodsButton")}
                  </Button>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="red"
                    onClick={() => setRemoveTarget(m)}
                  >
                    {t("pages.kpi.team.removeButton")}
                  </Button>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
          {!isLoading && members.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Text c="dimmed" size="sm" ta="center" py="sm">
                  {t("pages.kpi.team.empty")}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      {/* Historie: beendete Mitgliedschaften — so bleibt sichtbar, wann
          Kapazität zu- und abgenommen hat. */}
      {former.length > 0 && (
        <>
          <Text fw={600} size="sm" mt="lg" mb="xs">
            {t("pages.kpi.team.formerTitle", { count: former.length })}
          </Text>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("pages.kpi.team.colName")}</Table.Th>
                <Table.Th>{t("pages.kpi.team.periodFrom")}</Table.Th>
                <Table.Th>{t("pages.kpi.team.periodTo")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {former.map((f, i) => (
                <Table.Tr key={`${f.employeeId}-${f.memberUntil}-${i}`}>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {f.fullName}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {fmtDate(f.memberSince)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {fmtDate(f.memberUntil)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}

      <RemoveMemberModal
        segmentId={segmentId}
        target={removeTarget}
        onClose={() => setRemoveTarget(null)}
      />

      <EditSinceModal
        segmentId={segmentId}
        target={sinceTarget}
        onClose={() => setSinceTarget(null)}
      />

      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("pages.settings.users.createEmployeeTitle")}
      >
        <Text size="sm" c="dimmed" mb="sm">
          {t("pages.settings.users.createEmployeeHint")}
        </Text>
        <TextInput
          label={t("pages.settings.users.createEmployeeName")}
          value={newName}
          onChange={(e) => setNewName(e.currentTarget.value)}
          mb="sm"
        />
        <TextInput
          label={t("pages.settings.users.createEmployeeEmail")}
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.currentTarget.value)}
          mb="sm"
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setCreateOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={!newEmail.includes("@") || newName.trim().length === 0}
            loading={createEmployee.isPending}
            onClick={() =>
              createEmployee.mutate(
                { email: newEmail.trim(), fullName: newName.trim() },
                {
                  onSuccess: (res) => {
                    // Direkt dem Bereich zuordnen — dafür ist der Flow da.
                    saveProfile.mutate({
                      employeeId: res.id,
                      careerLevel: null,
                      federalState: "BE",
                      hoursPerDay: 8,
                      segmentId,
                      mentorEmployeeId: null,
                      yppDate: null,
                    });
                    setNewName("");
                    setNewEmail("");
                    setCreateOpen(false);
                  },
                },
              )
            }
          >
            {t("common.save")}
          </Button>
        </Group>
      </Modal>

      <HoursPeriodsModal
        target={periodsTarget}
        onClose={() => setPeriodsTarget(null)}
      />
    </Paper>
  );
}

/**
 * „Entfernen zum" — beendet die Mitgliedschaft datiert (auch rückwirkend
 * oder in der Zukunft). Die Kapazität des Bereichs bleibt bis einschließlich
 * des Austrittsdatums erhalten. Für Fehleingaben gibt es zusätzlich
 * „Komplett entfernen": löscht die Mitgliedschaft ersatzlos (keine Historie,
 * Kapazität auch rückwirkend bereinigt).
 */
function RemoveMemberModal({
  segmentId,
  target,
  onClose,
}: {
  segmentId: string;
  target: TeamMember | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const endMembership = useEndTeamMembership();
  const discardMembership = useDiscardTeamMembership();
  const [until, setUntil] = useState(isoToday());

  // Bereits terminierter Austritt → Dialog wird zum Korrektur-Dialog.
  useEffect(() => {
    if (target) setUntil(target.memberUntil ?? isoToday());
  }, [target]);

  return (
    <Modal
      opened={target !== null}
      onClose={onClose}
      title={
        target
          ? t("pages.kpi.team.removeTitle", { name: target.fullName })
          : ""
      }
      size="sm"
    >
      <Text size="sm" c="dimmed" mb="sm">
        {t("pages.kpi.team.removeHint")}
      </Text>
      <TextInput
        label={t("pages.kpi.team.removeUntilLabel")}
        type="date"
        value={until}
        onChange={(e) => setUntil(e.currentTarget.value)}
        mb="md"
        style={{ width: 180 }}
      />
      <Group justify="space-between">
        <Group gap={4}>
          <Button
            variant="subtle"
            color="red"
            size="compact-sm"
            title={t("pages.kpi.team.discardHint")}
            loading={discardMembership.isPending}
            onClick={() =>
              target &&
              discardMembership.mutate(
                { segmentId, employeeId: target.employeeId },
                { onSuccess: onClose },
              )
            }
          >
            {t("pages.kpi.team.discardButton")}
          </Button>
          {target?.memberUntil && (
            <Button
              variant="subtle"
              size="compact-sm"
              loading={endMembership.isPending}
              onClick={() =>
                endMembership.mutate(
                  {
                    segmentId,
                    employeeId: target.employeeId,
                    validTo: null,
                  },
                  { onSuccess: onClose },
                )
              }
            >
              {t("pages.kpi.team.reopenButton")}
            </Button>
          )}
        </Group>
        <Group gap="xs">
          <Button variant="default" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            color="red"
            disabled={!until}
            loading={endMembership.isPending}
            onClick={() =>
              target &&
              endMembership.mutate(
                { segmentId, employeeId: target.employeeId, validTo: until },
                { onSuccess: onClose },
              )
            }
          >
            {t("pages.kpi.team.removeConfirm")}
          </Button>
        </Group>
      </Group>
      <Text size="xs" c="dimmed" mt="sm">
        {t("pages.kpi.team.discardHint")}
      </Text>
    </Modal>
  );
}

/**
 * „Eintritt korrigieren" — setzt das Eintrittsdatum der aktuellen
 * Mitgliedschaft neu, OHNE einen Ehemaligen-Eintrag zu hinterlassen.
 * Typischer Fall: „+ Team" aus der Ausreißer-Analyse trägt immer heute ein.
 */
function EditSinceModal({
  segmentId,
  target,
  onClose,
}: {
  segmentId: string;
  target: TeamMember | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const redate = useRedateTeamMembership();
  const [from, setFrom] = useState("");

  // Beim Öffnen mit dem aktuellen Eintritt vorbelegen („seit jeher" → heute).
  const opened = target !== null;
  const value = from || target?.memberSince || isoToday();

  return (
    <Modal
      opened={opened}
      onClose={() => {
        setFrom("");
        onClose();
      }}
      title={
        target
          ? t("pages.kpi.team.sinceEditTitle", { name: target.fullName })
          : ""
      }
      size="sm"
    >
      <Text size="sm" c="dimmed" mb="sm">
        {t("pages.kpi.team.sinceEditHint")}
      </Text>
      <TextInput
        label={t("pages.kpi.team.joinFromLabel")}
        type="date"
        value={value}
        onChange={(e) => setFrom(e.currentTarget.value)}
        mb="md"
        style={{ width: 180 }}
      />
      <Group justify="flex-end">
        <Button
          variant="default"
          onClick={() => {
            setFrom("");
            onClose();
          }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          disabled={!value}
          loading={redate.isPending}
          onClick={() =>
            target &&
            redate.mutate(
              { segmentId, employeeId: target.employeeId, validFrom: value },
              {
                onSuccess: () => {
                  setFrom("");
                  onClose();
                },
              },
            )
          }
        >
          {t("pages.kpi.team.sinceEditConfirm")}
        </Button>
      </Group>
    </Modal>
  );
}

/**
 * Stunden-Zeitraeume: abweichende h/Tag in einem Datumsbereich (Elternteilzeit,
 * Reduktion). "bis" leer = unbefristet. Die Kapazitaet rechnet tagesgenau mit
 * dem jeweils gueltigen Wert — Vergangenheit bleibt korrekt.
 */
function HoursPeriodsModal({
  target,
  onClose,
}: {
  target: TeamMember | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: periods } = useHoursPeriods(target?.employeeId ?? null);
  const addPeriod = useAddHoursPeriod();
  const deletePeriod = useDeleteHoursPeriod();

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [hours, setHours] = useState<number | string>(6);

  return (
    <Modal
      opened={target !== null}
      onClose={onClose}
      title={
        target
          ? t("pages.kpi.team.periodsTitle", { name: target.fullName })
          : ""
      }
    >
      <Text size="sm" c="dimmed" mb="sm">
        {t("pages.kpi.team.periodsHint", {
          hours: target?.hoursPerDay ?? 8,
        })}
      </Text>

      <Table mb="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("pages.kpi.team.periodFrom")}</Table.Th>
            <Table.Th>{t("pages.kpi.team.periodTo")}</Table.Th>
            <Table.Th>{t("pages.kpi.team.colHours")}</Table.Th>
            <Table.Th style={{ width: 60 }} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(periods ?? []).map((p) => (
            <Table.Tr key={p.id}>
              <Table.Td>{p.validFrom}</Table.Td>
              <Table.Td>
                {p.validTo ?? t("pages.kpi.team.periodOpenEnded")}
              </Table.Td>
              <Table.Td>{p.hoursPerDay} h</Table.Td>
              <Table.Td>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="red"
                  onClick={() =>
                    target &&
                    deletePeriod.mutate({
                      employeeId: target.employeeId,
                      periodId: p.id,
                    })
                  }
                >
                  ✕
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
          {(periods ?? []).length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={4}>
                <Text c="dimmed" size="sm">
                  {t("pages.kpi.team.periodsEmpty")}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      <Group gap="xs" align="flex-end">
        <TextInput
          label={t("pages.kpi.team.periodFrom")}
          type="date"
          value={from}
          onChange={(e) => setFrom(e.currentTarget.value)}
          style={{ width: 150 }}
        />
        <TextInput
          label={t("pages.kpi.team.periodTo")}
          type="date"
          value={to}
          onChange={(e) => setTo(e.currentTarget.value)}
          style={{ width: 150 }}
        />
        <NumberInput
          label={t("pages.kpi.team.colHours")}
          value={hours}
          onChange={setHours}
          min={0}
          max={12}
          step={0.5}
          style={{ width: 90 }}
        />
        <Button
          size="compact-sm"
          disabled={!from || hours === "" || (to !== "" && to < from)}
          loading={addPeriod.isPending}
          onClick={() =>
            target &&
            addPeriod.mutate(
              {
                employeeId: target.employeeId,
                validFrom: from,
                validTo: to === "" ? null : to,
                hoursPerDay: Number(hours),
              },
              {
                onSuccess: () => {
                  setFrom("");
                  setTo("");
                },
              },
            )
          }
        >
          {t("pages.kpi.team.periodAdd")}
        </Button>
      </Group>
    </Modal>
  );
}
